import { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";
import { VideoLibraryItem } from "../../types/Community";
import {
  VIDEO_LIBRARY_ITEMS_FIELD_NAME,
  getCommunityVideoLibraryRef,
  migrateLegacyCommunityMedia,
} from "./communityMedia";
import {
  deleteMedia,
  uploadMedia,
} from "../media/cloudflareMedia";

const VIDEO_LIBRARY_FIELD = VIDEO_LIBRARY_ITEMS_FIELD_NAME;

const videoLibraryRef = (communityId: string) => getCommunityVideoLibraryRef(communityId);

const sanitizeFileNameSegment = (name: string): string =>
  name.replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 64) || "file";

const generateItemId = (): string =>
  `lib-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const buildStoragePath = (communityId: string, itemId: string, file: File): string => {
  const safeName = sanitizeFileNameSegment(file.name);
  return `${DB_PATH.COMMUNITY}/${communityId}/videoLibrary/${itemId}-${safeName}`;
};

// Keep the thumbnail at the SAME path depth as the main file uploads
// (videoLibrary/{fileName}) rather than a deeper videoLibrary/thumbs/ subfolder.
// The Storage security rules (managed in the console) are known to allow the
// single-segment upload path; a deeper path can be silently denied, which makes
// every thumbnail upload fail and the migration a no-op.
const buildThumbnailStoragePath = (communityId: string, itemId: string): string =>
  `${DB_PATH.COMMUNITY}/${communityId}/videoLibrary/${itemId}-thumb.jpg`;

const isDataUrl = (value: string | undefined): value is string =>
  typeof value === "string" && value.startsWith("data:");

// Decode a base64 data URL into a Blob so it can be uploaded to Storage.
const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, base64 = ""] = dataUrl.split(",", 2);
  const match = /data:([^;]+)/.exec(header);
  const contentType = match?.[1] || "image/jpeg";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: contentType });
};

// Thumbnails were originally inlined into the (single) library document as
// base64 data URLs, which pushed the doc past Firestore's 1 MiB per-document
// limit once a community accumulated enough media. We now host thumbnails in
// Storage and keep only the short download URL in the doc. Uploads a data-URL
// thumbnail to Storage and returns its public URL + storage path; on any
// failure it returns null so the caller can keep the inline data URL (a slightly
// bigger doc beats a broken preview).
const uploadThumbnailToStorage = async (
  communityId: string,
  itemId: string,
  dataUrl: string
): Promise<{
  url: string;
  path: string;
  storageProvider: "firebase" | "r2";
  mediaAssetId?: string;
} | null> => {
  try {
    const blob = dataUrlToBlob(dataUrl);
    const path = buildThumbnailStoragePath(communityId, itemId);
    const uploaded = await uploadMedia({
      body: blob,
      kind: "image",
      contentType: blob.type || "image/jpeg",
      fileName: `${itemId}-thumb.jpg`,
      firebasePath: path,
    });
    return {
      url: uploaded.url,
      path: uploaded.storagePath || "",
      storageProvider: uploaded.storageProvider,
      mediaAssetId: uploaded.mediaAssetId,
    };
  } catch (error) {
    console.warn("[videoLibrary] thumbnail upload to Storage failed", { itemId, error });
    return null;
  }
};

const VIDEO_PROBE_TIMEOUT_MS = 8000;

const probeVideoDuration = (file: File): Promise<number | undefined> =>
  new Promise((resolve) => {
    let done = false;
    const finish = (value: number | undefined) => {
      if (done) return;
      done = true;
      resolve(value);
    };
    try {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      const objectUrl = URL.createObjectURL(file);
      const cleanup = () => {
        try { URL.revokeObjectURL(objectUrl); } catch { /* noop */ }
      };
      const timer = window.setTimeout(() => {
        cleanup();
        finish(undefined);
      }, VIDEO_PROBE_TIMEOUT_MS);
      video.onloadedmetadata = () => {
        window.clearTimeout(timer);
        const duration = Number.isFinite(video.duration) ? video.duration : undefined;
        cleanup();
        finish(duration);
      };
      video.onerror = () => {
        window.clearTimeout(timer);
        cleanup();
        finish(undefined);
      };
      video.src = objectUrl;
    } catch {
      finish(undefined);
    }
  });

const generateVideoThumbnail = (file: File): Promise<string> =>
  new Promise((resolve) => {
    let done = false;
    const finish = (value: string) => {
      if (done) return;
      done = true;
      resolve(value);
    };
    try {
      const video = document.createElement("video");
      // "auto" is required so loadeddata + a real frame is available for drawImage
      video.preload = "auto";
      video.muted = true;
      video.playsInline = true;
      const objectUrl = URL.createObjectURL(file);
      const cleanup = () => {
        try { URL.revokeObjectURL(objectUrl); } catch { /* noop */ }
      };
      const timer = window.setTimeout(() => {
        console.warn("[videoLibrary] thumbnail timed out for", file.name);
        cleanup();
        finish("");
      }, VIDEO_PROBE_TIMEOUT_MS);

      const drawFrame = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = 160;
          canvas.height = 284;
          const ctx = canvas.getContext("2d");
          if (!ctx || !video.videoWidth || !video.videoHeight) {
            window.clearTimeout(timer);
            cleanup();
            finish("");
            return;
          }
          const scale = Math.max(canvas.width / video.videoWidth, canvas.height / video.videoHeight);
          const w = video.videoWidth * scale;
          const h = video.videoHeight * scale;
          ctx.drawImage(video, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
          window.clearTimeout(timer);
          cleanup();
          finish(dataUrl);
        } catch (error) {
          console.warn("[videoLibrary] thumbnail draw failed for", file.name, error);
          window.clearTimeout(timer);
          cleanup();
          finish("");
        }
      };

      video.onloadedmetadata = () => {
        // Seek ~2s in (or to the midpoint for short clips) to skip fade-in / black opening frames.
        const duration = Number.isFinite(video.duration) ? video.duration : 4;
        const target = Math.min(2.0, Math.max(0.3, duration / 2));
        try {
          video.currentTime = target;
        } catch {
          // Some browsers throw if currentTime is set too early; fall back to first frame
        }
      };
      video.onseeked = drawFrame;
      // Fallback path: some encodings won't seek but will fire loadeddata with a usable frame
      video.onloadeddata = () => {
        // Give onseeked a chance first; only draw here if seeking never resolves
        window.setTimeout(() => {
          if (!done) drawFrame();
        }, 1500);
      };
      video.onerror = () => {
        console.warn("[videoLibrary] video element errored for", file.name);
        window.clearTimeout(timer);
        cleanup();
        finish("");
      };
      video.src = objectUrl;
    } catch (error) {
      console.warn("[videoLibrary] thumbnail exception", error);
      finish("");
    }
  });

const IMAGE_THUMBNAIL_MAX_DIM = 400;

// Downscale the image onto a canvas instead of storing the full file as a
// base64 data URL. A full-size data URL easily exceeds Firestore's ~1 MiB
// document limit (the whole library lives in one doc), which broke image
// uploads. The real full-res image stays in Storage at mediaUrl.
const generateImageThumbnail = (file: File): Promise<string> =>
  new Promise((resolve) => {
    try {
      const objectUrl = URL.createObjectURL(file);
      const cleanup = () => {
        try { URL.revokeObjectURL(objectUrl); } catch { /* noop */ }
      };
      const img = new Image();
      img.onload = () => {
        try {
          const longest = Math.max(img.naturalWidth, img.naturalHeight) || 1;
          const scale = Math.min(1, IMAGE_THUMBNAIL_MAX_DIM / longest);
          const w = Math.max(1, Math.round(img.naturalWidth * scale));
          const h = Math.max(1, Math.round(img.naturalHeight * scale));
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            cleanup();
            resolve("");
            return;
          }
          ctx.drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
          cleanup();
          resolve(dataUrl);
        } catch (error) {
          console.warn("[videoLibrary] image thumbnail failed", error);
          cleanup();
          resolve("");
        }
      };
      img.onerror = () => {
        cleanup();
        resolve("");
      };
      img.src = objectUrl;
    } catch {
      resolve("");
    }
  });

export type UploadVideoLibraryItemOptions = {
  name?: string;
  tags?: string[];
  folder?: string;
  onProgress?: (percent: number) => void;
};

export const uploadVideoLibraryItem = async (
  communityId: string,
  file: File,
  options: UploadVideoLibraryItemOptions = {}
): Promise<VideoLibraryItem> => {
  if (!communityId) throw new Error("communityId is required");
  if (!file) throw new Error("file is required");

  const isVideo = file.type.startsWith("video/");
  const isImage = file.type.startsWith("image/");
  if (!isVideo && !isImage) {
    throw new Error(`Unsupported file type: ${file.type}`);
  }

  const itemId = generateItemId();
  const storagePath = buildStoragePath(communityId, itemId, file);
  const uploaded = await uploadMedia({
    body: file,
    kind: isVideo ? "video" : "image",
    fileName: file.name,
    firebasePath: storagePath,
    onProgress: options.onProgress
      ? (fraction) => options.onProgress!(fraction * 100)
      : undefined,
  });
  const mediaUrl = uploaded.url;

  const [durationSec, thumbnail] = await Promise.all([
    isVideo ? probeVideoDuration(file) : Promise.resolve<number | undefined>(undefined),
    isVideo ? generateVideoThumbnail(file) : generateImageThumbnail(file),
  ]);

  // Host the generated thumbnail in Storage rather than inlining the base64
  // data URL into the library document — inline thumbnails are what pushed the
  // single library doc past Firestore's 1 MiB limit. Fall back to the inline
  // data URL (or the media URL) only if the Storage upload fails.
  let mediaThumbnail = thumbnail || mediaUrl;
  let thumbnailStoragePath: string | undefined;
  let thumbnailMediaAssetId: string | undefined;
  if (isDataUrl(thumbnail)) {
    const hosted = await uploadThumbnailToStorage(communityId, itemId, thumbnail);
    if (hosted) {
      mediaThumbnail = hosted.url;
      thumbnailStoragePath = hosted.path || undefined;
      thumbnailMediaAssetId = hosted.mediaAssetId;
    }
  }

  const trimmedFolder = options.folder?.trim();
  const item: VideoLibraryItem = {
    id: itemId,
    name: options.name || file.name,
    type: isVideo ? "video" : "image",
    mediaUrl,
    mediaThumbnail,
    storagePath: uploaded.storagePath || "",
    storageProvider: uploaded.storageProvider,
    ...(uploaded.mediaAssetId ? { mediaAssetId: uploaded.mediaAssetId } : {}),
    sizeBytes: file.size,
    createdAt: new Date().toISOString(),
    ...(thumbnailStoragePath ? { thumbnailStoragePath } : {}),
    ...(thumbnailMediaAssetId ? { thumbnailMediaAssetId } : {}),
    ...(durationSec !== undefined ? { durationSec } : {}),
    ...(options.tags && options.tags.length ? { tags: options.tags } : {}),
    ...(trimmedFolder ? { folder: trimmedFolder } : {}),
  };

  // Append via a transaction rather than arrayUnion: the web SDK rejects
  // arrayUnion of a map that carries a nested array (tags) with "Property
  // array contains an invalid nested entity", and this matches how the
  // rename/folder/tags writers below mutate the list.
  const appendItem = () =>
    firestore.runTransaction(async (tx) => {
      const ref = videoLibraryRef(communityId);
      const snapshot = await tx.get(ref);
      const data = snapshot.data() || {};
      const current = Array.isArray((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD])
        ? ((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD] as VideoLibraryItem[])
        : [];
      tx.set(ref, { [VIDEO_LIBRARY_FIELD]: [...current, item] }, { merge: true });
    });

  try {
    await appendItem();
  } catch (error) {
    // The library lives in one Firestore doc capped at 1 MiB. Legacy items with
    // inline base64 thumbnails can fill it, so an append fails with "exceeds the
    // maximum allowed size". Shrink the doc by moving those thumbnails into
    // Storage, then retry the append once.
    if (isDocSizeError(error)) {
      console.warn("[videoLibrary] library doc over 1 MiB; migrating inline thumbnails and retrying", { communityId });
      const converted = await migrateInlineThumbnailsToStorage(communityId);
      if (converted > 0) {
        await appendItem();
        return item;
      }
      throw new Error(
        "This community's media library is full (Firestore's 1 MiB per-document limit) and could not be compacted automatically. Delete a few large items and try again."
      );
    }
    console.error("[videoLibrary] failed to write item to Firestore", { itemId, name: item.name, error });
    throw error;
  }

  return item;
};

const isDocSizeError = (error: unknown): boolean => {
  const message = (error as { message?: string })?.message || "";
  return /maximum allowed size|exceeds the maximum|longer than 1048487 bytes/i.test(message);
};

// Dedupe overlapping migration runs per community (each does a batch of Storage
// uploads). Holding the PROMISE — not just a boolean — lets a second caller
// (e.g. an upload that hit the size limit) await the in-flight run's completion
// instead of racing it.
const inlineThumbMigrations = new Map<string, Promise<number>>();

const THUMB_UPLOAD_CONCURRENCY = 6;

// One-time repair: move any inline base64 (data:) thumbnails in an existing
// library into Storage and rewrite the doc with short URLs. This is what pulls
// an over-1-MiB library document back under the limit so writes (uploads,
// renames, tag/folder edits) succeed again. Best effort: items whose thumbnail
// upload fails keep their inline data URL and get retried on a later run.
// Returns the number of items converted.
export const migrateInlineThumbnailsToStorage = (communityId: string): Promise<number> => {
  if (!communityId) return Promise.resolve(0);
  const existing = inlineThumbMigrations.get(communityId);
  if (existing) return existing;
  const run = runInlineThumbnailMigration(communityId).finally(() => {
    inlineThumbMigrations.delete(communityId);
  });
  inlineThumbMigrations.set(communityId, run);
  return run;
};

async function runInlineThumbnailMigration(communityId: string): Promise<number> {
  try {
    const snapshot = await videoLibraryRef(communityId).get();
    const data = snapshot.data() || {};
    const items = Array.isArray((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD])
      ? ((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD] as VideoLibraryItem[])
      : [];

    const pending = items.filter((item) => item && isDataUrl(item.mediaThumbnail));
    if (pending.length === 0) return 0;

    // Upload thumbnails outside the transaction (Storage I/O is slow); run them
    // in small concurrent batches so a large backlog doesn't take a minute.
    const hostedById = new Map<
      string,
      {
        url: string;
        path: string;
        storageProvider: "firebase" | "r2";
        mediaAssetId?: string;
      }
    >();
    for (let i = 0; i < pending.length; i += THUMB_UPLOAD_CONCURRENCY) {
      const batch = pending.slice(i, i + THUMB_UPLOAD_CONCURRENCY);
      const results = await Promise.all(
        batch.map(async (item) => {
          const hosted = await uploadThumbnailToStorage(communityId, item.id, item.mediaThumbnail);
          return [item.id, hosted] as const;
        })
      );
      for (const [id, hosted] of results) if (hosted) hostedById.set(id, hosted);
    }
    if (hostedById.size === 0) {
      console.warn(
        `[videoLibrary] inline thumbnail migration uploaded 0/${pending.length} thumbnails for ${communityId} (check Storage rules for the videoLibrary path)`
      );
      return 0;
    }

    // Re-read inside a transaction so concurrent uploads/edits aren't clobbered,
    // and apply the hosted thumbnails only to items still carrying a data URL.
    let converted = 0;
    await firestore.runTransaction(async (tx) => {
      converted = 0;
      const ref = videoLibraryRef(communityId);
      const snap = await tx.get(ref);
      const current = Array.isArray((snap.data() || {})[VIDEO_LIBRARY_FIELD])
        ? ((snap.data() as Record<string, unknown>)[VIDEO_LIBRARY_FIELD] as VideoLibraryItem[])
        : [];
      const next = current.map((item) => {
        const hosted = item && isDataUrl(item.mediaThumbnail) ? hostedById.get(item.id) : undefined;
        if (!hosted) return item;
        converted++;
        return {
          ...item,
          mediaThumbnail: hosted.url,
          ...(hosted.path ? { thumbnailStoragePath: hosted.path } : {}),
          ...(hosted.mediaAssetId
            ? { thumbnailMediaAssetId: hosted.mediaAssetId }
            : {}),
        };
      });
      if (converted === 0) return;
      tx.set(ref, { [VIDEO_LIBRARY_FIELD]: next }, { merge: true });
    });

    if (converted > 0) {
      console.info(`[videoLibrary] migrated ${converted} inline thumbnail(s) to Storage for ${communityId}`);
    }
    return converted;
  } catch (error) {
    console.warn("[videoLibrary] inline thumbnail migration failed", { communityId, error });
    return 0;
  }
}

export const getVideoLibrary = async (communityId: string): Promise<VideoLibraryItem[]> => {
  if (!communityId) return [];
  const snapshot = await videoLibraryRef(communityId).get();
  const data = snapshot.data();
  const raw = (data && (data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD]) as unknown;
  if (!Array.isArray(raw)) return [];
  return raw.filter((entry): entry is VideoLibraryItem =>
    Boolean(entry) &&
    typeof entry === "object" &&
    typeof (entry as VideoLibraryItem).id === "string" &&
    typeof (entry as VideoLibraryItem).mediaUrl === "string"
  );
};

export const listenToVideoLibrary = (
  communityId: string,
  onChange: (items: VideoLibraryItem[]) => void
): (() => void) => {
  if (!communityId) {
    onChange([]);
    return () => undefined;
  }
  void migrateLegacyCommunityMedia(communityId).catch((error) => {
    console.warn("[videoLibrary] legacy migration check failed", error);
  });
  // Move any inline base64 thumbnails into Storage. Self-terminating (no-op once
  // nothing is inline), so it's safe to fire on every library open.
  void migrateInlineThumbnailsToStorage(communityId).catch((error) => {
    console.warn("[videoLibrary] inline thumbnail migration check failed", error);
  });
  return videoLibraryRef(communityId).onSnapshot(
    (snapshot) => {
      const data = snapshot.data();
      const raw = (data && (data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD]) as unknown;
      if (!Array.isArray(raw)) {
        onChange([]);
        return;
      }
      const items = raw.filter((entry): entry is VideoLibraryItem =>
        Boolean(entry) &&
        typeof entry === "object" &&
        typeof (entry as VideoLibraryItem).id === "string" &&
        typeof (entry as VideoLibraryItem).mediaUrl === "string"
      );
      onChange(items);
    },
    (error) => {
      console.error("Error listening to video library:", error);
      onChange([]);
    }
  );
};

export const removeVideoLibraryItem = async (
  communityId: string,
  item: VideoLibraryItem
): Promise<void> => {
  if (!communityId || !item?.id) return;

  if (item.mediaUrl || item.storagePath) {
    try {
      await deleteMedia({
        url: item.mediaUrl,
        firebasePath: item.storagePath,
      });
    } catch (error) {
      console.warn("Failed to delete video library storage object:", error);
    }
  }

  if (
    item.mediaThumbnail &&
    item.mediaThumbnail !== item.mediaUrl &&
    (item.thumbnailStoragePath || item.thumbnailMediaAssetId)
  ) {
    try {
      await deleteMedia({
        url: item.mediaThumbnail,
        firebasePath: item.thumbnailStoragePath,
      });
    } catch (error) {
      console.warn("Failed to delete video library thumbnail object:", error);
    }
  }

  // Remove by id in a transaction. arrayRemove needs an exact deep-equality
  // match, so a single drifted field would leave the item stranded.
  await firestore.runTransaction(async (tx) => {
    const ref = videoLibraryRef(communityId);
    const snapshot = await tx.get(ref);
    const data = snapshot.data() || {};
    const current = Array.isArray((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD])
      ? ((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD] as VideoLibraryItem[])
      : [];
    const next = current.filter((entry) => entry?.id !== item.id);
    tx.set(ref, { [VIDEO_LIBRARY_FIELD]: next }, { merge: true });
  });
};

const generateThumbnailFromUrl = async (url: string): Promise<{ thumbnail: string; durationSec?: number }> => {
  // Fetch as a Blob so we can use a local object URL (sidesteps canvas-CORS issues).
  const response = await fetch(url);
  if (!response.ok) throw new Error(`fetch failed (${response.status})`);
  const blob = await response.blob();
  const fakeFile = new File([blob], "remote-video", { type: blob.type || "video/mp4" });
  const [thumbnail, durationSec] = await Promise.all([
    generateVideoThumbnail(fakeFile),
    probeVideoDuration(fakeFile),
  ]);
  return { thumbnail, durationSec };
};

export const backfillVideoLibraryThumbnail = async (
  communityId: string,
  item: VideoLibraryItem
): Promise<VideoLibraryItem | null> => {
  if (!communityId || !item?.id) return null;
  if (item.type !== "video") return null;

  let regenerated: { thumbnail: string; durationSec?: number };
  try {
    regenerated = await generateThumbnailFromUrl(item.mediaUrl);
  } catch (error) {
    console.warn("[videoLibrary] backfill: could not fetch video for", item.name, error);
    return null;
  }

  if (!regenerated.thumbnail) {
    console.warn("[videoLibrary] backfill: thumbnail generation returned empty for", item.name);
    return null;
  }

  const updated: VideoLibraryItem = {
    ...item,
    mediaThumbnail: regenerated.thumbnail,
    ...(regenerated.durationSec !== undefined ? { durationSec: regenerated.durationSec } : {}),
  };

  await firestore.runTransaction(async (tx) => {
    const ref = videoLibraryRef(communityId);
    const snapshot = await tx.get(ref);
    const data = snapshot.data() || {};
    const current = Array.isArray((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD])
      ? ((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD] as VideoLibraryItem[])
      : [];
    const next = current.map((entry) => (entry.id === item.id ? updated : entry));
    tx.set(ref, { [VIDEO_LIBRARY_FIELD]: next }, { merge: true });
  });

  return updated;
};

export const renameVideoLibraryItem = async (
  communityId: string,
  item: VideoLibraryItem,
  nextName: string
): Promise<VideoLibraryItem> => {
  const trimmed = nextName.trim();
  if (!communityId || !item?.id || !trimmed || trimmed === item.name) return item;

  const updated: VideoLibraryItem = { ...item, name: trimmed };
  await firestore.runTransaction(async (tx) => {
    const ref = videoLibraryRef(communityId);
    const snapshot = await tx.get(ref);
    const data = snapshot.data() || {};
    const current = Array.isArray((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD])
      ? ((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD] as VideoLibraryItem[])
      : [];
    const next = current.map((entry) => (entry.id === item.id ? updated : entry));
    tx.set(ref, { [VIDEO_LIBRARY_FIELD]: next }, { merge: true });
  });
  return updated;
};

export const setVideoLibraryItemTags = async (
  communityId: string,
  item: VideoLibraryItem,
  nextTags: string[]
): Promise<VideoLibraryItem> => {
  if (!communityId || !item?.id) return item;
  const tags = Array.from(
    new Set(nextTags.map((t) => t.trim().toLowerCase()).filter(Boolean))
  );
  const updated: VideoLibraryItem = tags.length
    ? { ...item, tags }
    : (() => {
        const { tags: _omit, ...rest } = item;
        return rest as VideoLibraryItem;
      })();

  await firestore.runTransaction(async (tx) => {
    const ref = videoLibraryRef(communityId);
    const snapshot = await tx.get(ref);
    const data = snapshot.data() || {};
    const current = Array.isArray((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD])
      ? ((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD] as VideoLibraryItem[])
      : [];
    const next = current.map((entry) => (entry.id === item.id ? updated : entry));
    tx.set(ref, { [VIDEO_LIBRARY_FIELD]: next }, { merge: true });
  });
  return updated;
};

export const setVideoLibraryItemFolder = async (
  communityId: string,
  item: VideoLibraryItem,
  nextFolder: string | null
): Promise<VideoLibraryItem> => {
  if (!communityId || !item?.id) return item;
  const trimmed = nextFolder?.trim() || "";
  const updated: VideoLibraryItem = trimmed
    ? { ...item, folder: trimmed }
    : (() => {
        const { folder: _omit, ...rest } = item;
        return rest as VideoLibraryItem;
      })();

  await firestore.runTransaction(async (tx) => {
    const ref = videoLibraryRef(communityId);
    const snapshot = await tx.get(ref);
    const data = snapshot.data() || {};
    const current = Array.isArray((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD])
      ? ((data as Record<string, unknown>)[VIDEO_LIBRARY_FIELD] as VideoLibraryItem[])
      : [];
    const next = current.map((entry) => (entry.id === item.id ? updated : entry));
    tx.set(ref, { [VIDEO_LIBRARY_FIELD]: next }, { merge: true });
  });
  return updated;
};
